import React from 'react';

import { useIntl } from 'react-intl';

import { LIST_SITES_RESOURCE } from 'src/features/sites/api/useListSites';
import { useUpdateSite } from 'src/features/sites/api/useUpdateSite';
import SiteFormModal from 'src/features/sites/components/SiteFormModal';
import { siteMessages } from 'src/features/sites/utils/messages';
import { Site } from 'src/features/sites/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';

interface SiteFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSite: Site;
    children?: React.ReactNode;
}

function UpdateSiteFormModal<TData>({ className, semantics, children, selectedSite }: SiteFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateSite({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SITES_RESOURCE] });
            toast({
                title: formatMessage(siteMessages.updateSiteSuccess),
                description: formatMessage(siteMessages.updateSiteSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });
    return (
        <SiteFormModal
            selectedSite={selectedSite}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, siteId: selectedSite.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(siteMessages.editSiteSubtitle, {
                entry: selectedSite.translations.find((translation) => translation.language === locale)?.name,
            })}
        >
            {children}
        </SiteFormModal>
    );
}

export default UpdateSiteFormModal;
